import { Link, Navigate } from '@tanstack/react-router'
import type { MeResponse, PartnerView, ProgramStatus, ProgramVersionView } from '@tex/finance-shared'
import { type FormEvent, useState } from 'react'
import { Alert, Button, Card, Field, Input, Mono, Row, Textarea } from '../components/ui'
import { errorMessage } from '../lib/api'
import { formatDate, formatDay, humanize } from '../lib/format'
import { useCreateProgram, useNewProgramVersion, usePartner, usePartnerCommand, usePartnerPrograms, useProgramCommand } from '../lib/programs'
import { isTexStaff, useMe } from '../lib/session'
import { PartnerStatusBadge } from './partners-list'

const programTone: Record<ProgramStatus, string> = {
  draft: 'bg-neutral-100 text-neutral-800',
  test_only: 'bg-amber-100 text-amber-900',
  active: 'bg-green-100 text-green-900',
  retired: 'bg-neutral-200 text-neutral-500',
}

export function ProgramStatusBadge({ status }: { status: ProgramStatus }) {
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${programTone[status]}`}>{humanize(status)}</span>
}

export function PartnerDetailPage({ partnerId }: { partnerId: string }) {
  const me = useMe()
  const partner = usePartner(partnerId)
  const programs = usePartnerPrograms(partnerId)

  if (me.data === null) return <Navigate to="/sign-in" />
  if (me.data && !isTexStaff(me.data)) return <Alert tone="error">Only Tex staff can see funding partners.</Alert>
  if (partner.isPending || programs.isPending || !me.data) return <p className="text-sm text-neutral-500">Loading…</p>
  if (partner.isError) return <Alert tone="error">{errorMessage(partner.error)}</Alert>
  if (programs.isError) return <Alert tone="error">{errorMessage(programs.error)}</Alert>

  const p = partner.data.partner
  const isOps = me.data.tex_role === 'ops_admin'

  return (
    <section className="space-y-6">
      <div>
        <Link to="/tex/partners" className="text-sm text-neutral-500 hover:text-neutral-800">
          ← Partners
        </Link>
        <h1 className="mt-1 flex flex-wrap items-center gap-3 font-display text-3xl font-bold text-neutral-900">
          {p.display_name}
          <PartnerStatusBadge status={p.status} />
          {p.is_synthetic && <span className="rounded bg-brand-50 px-1.5 py-0.5 text-xs font-medium text-tex-blue">synthetic</span>}
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          {p.legal_name} · <Mono>{p.code}</Mono>
        </p>
      </div>

      {p.status_reason && (
        <Alert tone="info">
          {humanize(p.status)}: {p.status_reason}
        </Alert>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <Card title="Agreement and access">
          <dl>
            <Row label="Submission method" value={humanize(p.submission_method)} />
            <Row label="Submission instructions" value={p.submission_instructions} />
            <Row label="Agreement reference" value={p.agreement_reference} />
            <Row label="Agreement effective" value={p.agreement_effective_from ? `${formatDay(p.agreement_effective_from)} → ${formatDay(p.agreement_effective_to)}` : null} />
            <Row label="Access verified" value={p.access_verified_at ? formatDate(p.access_verified_at) : 'not verified'} />
            <Row label="Created" value={formatDate(p.created_at)} />
          </dl>
        </Card>
        {isOps && <PartnerStatusCard partner={p} />}
      </div>

      <ProgramsCard me={me.data} partner={p} programs={programs.data.programs} />
    </section>
  )
}

function PartnerStatusCard({ partner }: { partner: PartnerView }) {
  const command = usePartnerCommand(partner.id)
  const [agreementRef, setAgreementRef] = useState(partner.agreement_reference ?? '')
  const [effectiveFrom, setEffectiveFrom] = useState(partner.agreement_effective_from ?? '')
  const [reason, setReason] = useState('')

  function activate(e: FormEvent) {
    e.preventDefault()
    command.mutate({
      command: 'activate',
      agreement_reference: agreementRef.trim(),
      agreement_effective_from: effectiveFrom,
      access_verified_at: new Date().toISOString(),
    })
  }

  if (partner.status === 'terminated') {
    return (
      <Card title="Status">
        <p className="text-sm text-neutral-500">Terminated partners cannot change status. Create a new partner record if the relationship resumes.</p>
      </Card>
    )
  }

  return (
    <Card title="Status commands">
      <div className="space-y-4">
        {partner.status !== 'active' && (
          <form onSubmit={activate} className="space-y-3">
            <p className="text-xs text-neutral-500">
              Activating records that the agreement is in place and that Tex verified access to the partner&apos;s submission channel right
              now.
            </p>
            <Field label="Agreement reference">
              <Input required value={agreementRef} onChange={(e) => setAgreementRef(e.target.value)} />
            </Field>
            <Field label="Agreement effective from">
              <Input type="date" required value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
            </Field>
            <Button type="submit" disabled={command.isPending}>
              Activate (marks access verified now)
            </Button>
          </form>
        )}
        <div className="space-y-3 border-t border-neutral-100 pt-3">
          <Field label="Reason" hint="Required to suspend or terminate; recorded on the partner and in the audit log.">
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <div className="flex gap-2">
            {partner.status === 'active' && (
              <Button variant="secondary" disabled={command.isPending || reason.trim().length < 2} onClick={() => command.mutate({ command: 'suspend', reason: reason.trim() })}>
                Suspend
              </Button>
            )}
            <Button variant="danger" disabled={command.isPending || reason.trim().length < 2} onClick={() => command.mutate({ command: 'terminate', reason: reason.trim() })}>
              Terminate
            </Button>
          </div>
        </div>
        {command.isError && <Alert tone="error">{errorMessage(command.error)}</Alert>}
      </div>
    </Card>
  )
}

function ProgramsCard({ me, partner, programs }: { me: MeResponse; partner: PartnerView; programs: ProgramVersionView[] }) {
  const isOps = me.tex_role === 'ops_admin'
  const byCode = new Map<string, ProgramVersionView[]>()
  for (const pv of programs) {
    const list = byCode.get(pv.program_code) ?? []
    list.push(pv)
    byCode.set(pv.program_code, list)
  }

  return (
    <Card title="Program versions">
      <div className="space-y-6">
        {byCode.size === 0 && <p className="text-sm text-neutral-500">No programs yet.</p>}
        {[...byCode.entries()].map(([code, versions]) => (
          <div key={code}>
            <h3 className="mb-2 text-sm font-semibold">
              <Mono>{code}</Mono>
            </h3>
            <table className="w-full text-sm">
              <thead className="text-left font-display text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="py-1 pr-3">Version</th>
                  <th className="py-1 pr-3">Status</th>
                  <th className="py-1 pr-3">Title</th>
                  <th className="py-1 pr-3">Effective</th>
                  <th className="py-1 pr-3">Criteria verified</th>
                  <th className="py-1 pr-3 text-right">Rules</th>
                  {isOps && <th className="py-1" />}
                </tr>
              </thead>
              <tbody>
                {[...versions]
                  .sort((a, b) => b.version - a.version)
                  .map((pv) => (
                    <tr key={pv.id} className="border-t border-neutral-100 transition-colors hover:bg-neutral-50/60">
                      <td className="py-1.5 pr-3">
                        <Link to="/tex/programs/$programId" params={{ programId: pv.id }} className="font-medium hover:underline">
                          v{pv.version}
                        </Link>
                      </td>
                      <td className="py-1.5 pr-3">
                        <ProgramStatusBadge status={pv.status} />
                      </td>
                      <td className="py-1.5 pr-3">{pv.title}</td>
                      <td className="py-1.5 pr-3 text-neutral-500">
                        {pv.effective_from ? `${formatDay(pv.effective_from)} → ${pv.effective_to ? formatDay(pv.effective_to) : 'open'}` : '—'}
                      </td>
                      <td className="py-1.5 pr-3 text-neutral-500">{pv.criteria_verified_at ? formatDate(pv.criteria_verified_at) : 'unverified'}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{pv.rules.length}</td>
                      {isOps && (
                        <td className="py-1.5 text-right">
                          <ProgramRowActions program={pv} partner={partner} />
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ))}
        {isOps && partner.status !== 'terminated' && <CreateProgramForm partnerId={partner.id} />}
      </div>
    </Card>
  )
}

function ProgramRowActions({ program, partner }: { program: ProgramVersionView; partner: PartnerView }) {
  const command = useProgramCommand(program)
  const newVersion = useNewProgramVersion(program)
  const busy = command.isPending || newVersion.isPending
  const error = command.error ?? newVersion.error

  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {program.status === 'draft' && (
        <Button variant="secondary" className="!px-2 !py-0.5 text-xs" disabled={busy} onClick={() => command.mutate({ command: 'mark_test_only', ...(program.effective_from ? {} : { effective_from: new Date().toISOString().slice(0, 10) }) })}>
          Mark test-only
        </Button>
      )}
      {(program.status === 'draft' || program.status === 'test_only') && (
        <Button
          className="!px-2 !py-0.5 text-xs"
          disabled={busy || partner.status !== 'active'}
          title={partner.status !== 'active' ? 'Activate the partner first' : 'Confirms the criteria were verified against partner documentation now'}
          onClick={() =>
            command.mutate({
              command: 'activate',
              criteria_verified_at: new Date().toISOString(),
              ...(program.effective_from || program.status !== 'draft' ? {} : { effective_from: new Date().toISOString().slice(0, 10) }),
            })
          }
        >
          Activate
        </Button>
      )}
      {program.status !== 'retired' && program.status !== 'draft' && (
        <Button variant="secondary" className="!px-2 !py-0.5 text-xs" disabled={busy} onClick={() => newVersion.mutate({})}>
          New version
        </Button>
      )}
      {(program.status === 'test_only' || program.status === 'active') && (
        <Button variant="danger" className="!px-2 !py-0.5 text-xs" disabled={busy} onClick={() => command.mutate({ command: 'retire' })}>
          Retire
        </Button>
      )}
      {error && <span className="basis-full text-right text-xs text-tex-red">{errorMessage(error)}</span>}
    </div>
  )
}

function CreateProgramForm({ partnerId }: { partnerId: string }) {
  const create = useCreateProgram(partnerId)
  const [code, setCode] = useState('')
  const [title, setTitle] = useState('')

  function submit(e: FormEvent) {
    e.preventDefault()
    create.mutate(
      { program_code: code.trim(), title: title.trim(), rules: [], document_requirements: [] },
      { onSuccess: () => (setCode(''), setTitle('')) },
    )
  }

  return (
    <form onSubmit={submit} className="grid gap-3 border-t border-neutral-100 pt-4 sm:grid-cols-[1fr_2fr_auto] sm:items-end">
      <Field label="New program code" hint="Globally unique slug; version 1 starts as a draft.">
        <Input required pattern="[a-z0-9][a-z0-9-]{1,63}" value={code} onChange={(e) => setCode(e.target.value)} />
      </Field>
      <Field label="Title">
        <Input required value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Button type="submit" disabled={create.isPending}>
        Create draft
      </Button>
      {create.isError && (
        <div className="sm:col-span-3">
          <Alert tone="error">{errorMessage(create.error)}</Alert>
        </div>
      )}
      {create.isSuccess && (
        <div className="sm:col-span-3">
          <Alert tone="success">
            Draft created.{' '}
            <Link to="/tex/programs/$programId" params={{ programId: create.data.program.id }} className="underline">
              Edit its rules
            </Link>
            .
          </Alert>
        </div>
      )}
    </form>
  )
}
